import { type FC, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Draggable } from 'react-beautiful-dnd';
import { BsCaretDown, BsThreeDotsVertical } from 'react-icons/bs';

import { slideLayoutLabels } from '@soomo/lib/components/pageElements/Carousel/constants';

import { slideStyles } from './styles';
import SlidePreview from './SlidePreview';
import SlideDropdown from './SlideDropdown';
import IconButton from '@editor2/components/IconButton';
import CopyInput from '@editor2/components/CopyInputWrapper';
import { useEventListener } from '@editor2/hooks/useEventListener';

import {
	getDefaultLayoutProps,
	getSlidePreviewText,
	isWithImage,
	isWithText,
	isWithTitle,
	showLayoutSwitchWarning
} from './utils';
import RichTextEditor from '../HtmlElement/RichTextEditor';
import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import { type ToolbarOption } from '../HtmlElement/EditorToolbar';
import { CollapseState } from '@editor2/types/collapseState';
import {
	CarouselTheme,
	ImagePositionEnum,
	QuotationSizeEnum,
	type SlideContent,
	SlideLayoutEnum
} from '@soomo/lib/types/index';
import { type CarouselSlide } from '@editor2/types/pageElements';

interface Props {
	slide: CarouselSlide;
	index: number;
	expandAll: CollapseState;
	setExpandAll?: (value: CollapseState) => void;
	theme: CarouselTheme;
	color: string;
	logo_url?: string;
	handleSlideChange: (value: Partial<CarouselSlide>, index: number) => void;
	handleAddSlide: (index?: number) => void;
	handleDuplicateSlide: (index?: number) => void;
	handleDeleteSlide: (index?: number) => void;
}

const Slide: FC<Props> = (props) => {
	const {
		slide,
		index,
		expandAll,
		setExpandAll,
		theme,
		color,
		logo_url,
		handleSlideChange,
		handleAddSlide,
		handleDuplicateSlide,
		handleDeleteSlide
	} = props;
	const { content, hide_logo } = slide;

	const [collapsed, setCollapsed] = useState(true);
	const [isDropdownShown, setDropdownShown] = useState(false);
	const dropdownRef = useRef(null);

	const handleClickOutside = (event) => {
		if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setDropdownShown(false);
	};
	useEventListener('click', handleClickOutside);

	useEffect(() => {
		if (expandAll === CollapseState.CollapseAll) setCollapsed(true);
		if (expandAll === CollapseState.ExpandAll) setCollapsed(false);
		if (expandAll === CollapseState.RevealAllHTML) {
			setCollapsed(true);
			if (isWithText(content) || isWithTitle(content)) setCollapsed(false);
		}
	}, [expandAll, content]);

	const handleSlideContentChange = (value: Partial<SlideContent>) => {
		handleSlideChange({ content: { ...content, ...value } as SlideContent }, index);
	};

	return (
		<Draggable draggableId={slide.family_id} index={index}>
			{(provided) => (
				<div ref={provided.innerRef} {...provided.draggableProps}>
					<div>
						<div className={slideStyles}>
							<div
								{...provided.dragHandleProps}
								className="header"
								onClick={() => {
									setCollapsed(!collapsed);
									setExpandAll(CollapseState.Mixed);
								}}>
								<div>
									Slide {index + 1}: {getSlidePreviewText(content)}...
								</div>
								<div className="tools">
									<BsCaretDown className={collapsed ? 'collapseIconRotate' : 'collapseIcon'} />
									<span ref={dropdownRef} className="options-icon">
										<IconButton
											onClick={(e) => {
												e.stopPropagation();
												setDropdownShown(!isDropdownShown);
											}}>
											<BsThreeDotsVertical />
										</IconButton>

										{isDropdownShown && (
											<SlideDropdown
												index={index}
												onDropdownHide={() => setDropdownShown(false)}
												handleAddSlide={handleAddSlide}
												handleDuplicateSlide={handleDuplicateSlide}
												handleDeleteSlide={handleDeleteSlide}
											/>
										)}
									</span>
								</div>
							</div>

							{!collapsed && (
								<div className="slide-body">
									<Form.Group>
										<Form.Label>Slide preview</Form.Label>
										<SlidePreview
											key={slide.family_id}
											slide={slide}
											theme={theme}
											color={color}
											logo_url={logo_url}
											scale={1}
										/>
									</Form.Group>

									<div style={{ width: '100%' }}>
										<Form.Group>
											<Form.Label>Slide Layout</Form.Label>
											<Form.Control
												as="select"
												className="select"
												value={content.layout}
												onChange={(e) => {
													const newLayout = e.target.value as SlideLayoutEnum;
													const newContent = getDefaultLayoutProps(newLayout, content);
													const shouldWarn = showLayoutSwitchWarning(content, newContent);

													if (
														!shouldWarn ||
														window.confirm(
															'Changing the layout may remove some content. Do you want to continue?'
														)
													) {
														handleSlideChange(
															{ content: { layout: newLayout, ...newContent } as SlideContent },
															index
														);
													}
												}}>
												{Object.values(SlideLayoutEnum).map((type) => (
													<option key={type} value={type}>
														{slideLayoutLabels[type]}
													</option>
												))}
											</Form.Control>
										</Form.Group>
										{content.layout === SlideLayoutEnum.TextImage && (
											<Form.Group>
												<Form.Label>Image position</Form.Label>
												<Form.Control
													as="select"
													className="select"
													value={content.position}
													onChange={(e) => {
														handleSlideContentChange({
															position: e.target.value as ImagePositionEnum
														});
													}}>
													{Object.values(ImagePositionEnum).map((type) => (
														<option key={type} value={type}>
															{type}
														</option>
													))}
												</Form.Control>
											</Form.Group>
										)}
										{content.layout === SlideLayoutEnum.Quotation && (
											<Form.Group>
												<Form.Label>Quotation size</Form.Label>
												<Form.Control
													as="select"
													className="select"
													value={content.size}
													onChange={(e) => {
														handleSlideContentChange({
															size: e.target.value as QuotationSizeEnum
														});
													}}>
													{Object.values(QuotationSizeEnum).map((type) => (
														<option key={type} value={type}>
															{type}
														</option>
													))}
												</Form.Control>
											</Form.Group>
										)}
										{isWithTitle(content) && (
											<Form.Group>
												<Form.Label>Title</Form.Label>
												<CodeMirrorHTML
													value={content.title}
													onChange={(value) => handleSlideContentChange({ title: value })}
													showTips={false}
												/>
												{content.layout === SlideLayoutEnum.TitleSubtitle && (
													<>
														<Form.Label>Subtitle</Form.Label>
														<CodeMirrorHTML
															value={content.subtitle}
															onChange={(value) => handleSlideContentChange({ subtitle: value })}
															showTips={false}
														/>
													</>
												)}
											</Form.Group>
										)}

										{isWithText(content) && (
											<RichTextEditor
												toolbar_id={slide.family_id}
												text={content.text}
												onChange={(value) => handleSlideContentChange({ text: value })}
												allowedOptions={[
													...(content.layout !== SlideLayoutEnum.Quotation
														? (['h2', 'eyebrowHeading'] as ToolbarOption[])
														: []),
													'bold',
													'italic',
													'list-ordered',
													'list-bullet',
													'link',
													'script-sub',
													'script-super',
													'rolloverText'
												]}
												forceCodeMode={expandAll === CollapseState.RevealAllHTML}
											/>
										)}

										{isWithImage(content) && (
											<ImageFields
												url={content.url}
												alt={content.alt}
												credit={content.credit}
												description={content.description}
												onChange={(value) => handleSlideContentChange(value)}
											/>
										)}

										{content.layout === SlideLayoutEnum.Quotation && (
											<>
												<Form.Label>Attribution</Form.Label>
												<CodeMirrorHTML
													value={content.attribution}
													onChange={(value) => handleSlideContentChange({ attribution: value })}
													showTips={false}
												/>
											</>
										)}

										{theme === CarouselTheme.Permissioned && (
											<Form.Group>
												<Form.Check
													type="checkbox"
													checked={hide_logo}
													onChange={() => handleSlideChange({ hide_logo: !hide_logo }, index)}
													label="Hide logo"
												/>
											</Form.Group>
										)}

										<CopyInput label="Family ID" value={slide.family_id} />
									</div>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</Draggable>
	);
};

export default Slide;

interface ImageFieldsProps {
	url: string;
	alt: string;
	credit: string;
	description: string;
	onChange: (
		value: Partial<{ url: string; alt: string; credit: string; description: string }>
	) => void;
}
const ImageFields: FC<ImageFieldsProps> = ({ url, alt, credit, description, onChange }) => (
	<Form.Group>
		<Form.Label>Image URL</Form.Label>
		<Form.Control value={url} onChange={(e) => onChange({ url: e.target.value })} />
		<Form.Label>Image alt tag</Form.Label>
		<Form.Control
			as="textarea"
			rows={1}
			value={alt}
			onChange={(e) => onChange({ alt: e.target.value })}
		/>
		<Form.Label>Image credit</Form.Label>
		<CodeMirrorHTML
			value={credit}
			onChange={(value) => onChange({ credit: value })}
			showTips={false}
		/>
		<Form.Label>Image description</Form.Label>
		<Form.Control
			as="textarea"
			rows={1}
			value={description}
			onChange={(e) => onChange({ description: e.target.value })}
		/>
	</Form.Group>
);
